import * as Sentry from "@sentry/browser";

const PII_HEADER_DENYLIST = ["authorization", "cookie", "forwarded", "-ip", "remote-", "via", "-user"];

const IGNORED_ERRORS: Sentry.BrowserOptions["ignoreErrors"] = [
  "TypeError: Load failed",
  "TypeError: Failed to fetch",
  "TypeError: NetworkError when attempting to fetch resource.",
  "TypeError: cancelled",
  "454: Handling is disabled",
  /^Non-Error promise rejection captured with value: Request timeout \w+$/,
];

const IGNORED_SPANS: Sentry.BrowserOptions["ignoreSpans"] = [
  { op: /^resource\./ },
  { op: /^browser\.(dns|connect|tls_ssl|cache|redirect|request|response|unload_event)$/ },
];

export function isFirstPartyRequest(url: string): boolean {
  return new URL(url, window.location.href).origin === window.location.origin;
}

// Google Translate rewrites the DOM, which causes crashes that are not our bugs (facebook/react#11538).
// Chrome on iOS skips the html classes but still wraps text in <font style="vertical-align: inherit">.
function isPageTranslated(): boolean {
  const classes = document.documentElement.classList;
  return (
    classes.contains("translated-ltr") ||
    classes.contains("translated-rtl") ||
    document.querySelector("font[style*='vertical-align']") !== null
  );
}

export function initSentry({
  dsn,
  environment,
  release,
  tracesSampleRate,
  syntheticUserAgentToken,
}: SentryBrowserSettings): void {
  if (!dsn) return;

  const isSyntheticTraffic = navigator.userAgent.includes(syntheticUserAgentToken);

  Sentry.init({
    dsn,
    environment,
    release: release ?? undefined,
    allowUrls: [`${window.location.origin}/js/`],
    integrations: [Sentry.browserTracingIntegration({ shouldCreateSpanForRequest: isFirstPartyRequest })],
    ignoreErrors: IGNORED_ERRORS,
    ignoreSpans: IGNORED_SPANS,
    tracesSampleRate: isSyntheticTraffic ? 0 : tracesSampleRate,
    initialScope: isSyntheticTraffic ? { tags: { synthetic: true } } : undefined,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { deny: PII_HEADER_DENYLIST },
        response: { deny: PII_HEADER_DENYLIST },
      },
      httpBodies: [],
      urlQueryParams: false,
    },
    beforeSend: (event) => {
      event.tags = { ...event.tags, translated: String(isPageTranslated()) };
      return event;
    },
  });
}
